import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Landmark, Percent, Plus, Trash2, Save, Loader2, Users, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
  const { data: fees = [] } = useQuery({ queryKey: ["bank-fees"], queryFn: () => api.get("/bank-fees").then((r) => r.data) });
  const { data: referrals = [] } = useQuery({ queryKey: ["referrals-all"], queryFn: () => api.get("/referrals").then((r) => r.data) });
  const { data: rates = {} } = useQuery({ queryKey: ["referral-rates"], queryFn: () => api.get("/referral-rates").then((r) => r.data) });
  const { data: protectedData } = useQuery({ queryKey: ["protected-fields"], queryFn: () => api.get("/settings/protected-fields").then((r) => r.data) });

  const [protectedSel, setProtectedSel] = useState(null);
  useEffect(() => { if (protectedData) setProtectedSel(protectedData.fields); }, [protectedData]);

  const FIELD_LABELS = {
    name: "Full Name", first_name: "First Name", last_name: "Last Name", email: "Email", phone: "Phone",
    country: "Country", account_type: "Account Type", kyc_status: "KYC Status", status: "Account Status",
    referred_by: "Referred By", referral_code: "Referral Code", deposits: "Deposits", balance: "Balance", created_date: "Created Date",
  };

  const toggleProtected = (f) => setProtectedSel((sel) => (sel.includes(f) ? sel.filter((x) => x !== f) : [...sel, f]));

  const saveProtected = async () => {
    setBusy(true);
    try {
      await api.put("/settings/protected-fields", { fields: protectedSel });
      qc.invalidateQueries({ queryKey: ["protected-fields"] });
      toast.success("Protected fields saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const [defaultRate, setDefaultRate] = useState("");
  const [newFee, setNewFee] = useState({ provider: "", currency: "ALL", percent: "", fixed: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (settings) setDefaultRate(String(settings.default_referral_rate)); }, [settings]);

  const saveDefault = async () => {
    setBusy(true);
    try {
      await api.put("/settings", { default_referral_rate: parseFloat(defaultRate) || 0 });
      toast.success("Default rate saved");
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["profit"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const addFee = async () => {
    if (!newFee.provider.trim()) { toast.error("Provider is required"); return; }
    setBusy(true);
    try {
      await api.post("/bank-fees", {
        provider: newFee.provider.trim(),
        currency: newFee.currency.trim() || "ALL",
        percent: parseFloat(newFee.percent) || 0,
        fixed: parseFloat(newFee.fixed) || 0,
      });
      toast.success("Bank fee added");
      setNewFee({ provider: "", currency: "ALL", percent: "", fixed: "" });
      qc.invalidateQueries({ queryKey: ["bank-fees"] });
      qc.invalidateQueries({ queryKey: ["profit"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const deleteFee = async (id) => {
    try {
      await api.delete(`/bank-fees/${id}`);
      qc.invalidateQueries({ queryKey: ["bank-fees"] });
      qc.invalidateQueries({ queryKey: ["profit"] });
      toast.success("Removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveFee = async (id, patch) => {
    try {
      await api.patch(`/bank-fees/${id}`, patch);
      qc.invalidateQueries({ queryKey: ["bank-fees"] });
      qc.invalidateQueries({ queryKey: ["profit"] });
      toast.success("Bank fee updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveRate = async (code, value) => {
    try {
      const rate = value === "" ? null : parseFloat(value);
      await api.put(`/referral-rates/${encodeURIComponent(code)}`, { rate });
      qc.invalidateQueries({ queryKey: ["referral-rates"] });
      qc.invalidateQueries({ queryKey: ["profit"] });
      toast.success(`${code} rate ${rate === null ? "reset to default" : "set to " + rate + "%"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const syncProviders = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bank-fees/sync");
      qc.invalidateQueries({ queryKey: ["bank-fees"] });
      toast.success(data.added > 0 ? `Added ${data.added} new provider(s) from transactions` : "All transaction providers are already listed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!isAdmin) {
    return (
      <div className="space-y-6" data-testid="settings-page">
        <h1 className="font-heading text-3xl font-semibold tracking-tight">Settings</h1>
        <div className="rounded-md border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Only admins can manage commission rates and bank fees.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">Configure affiliate commission rates and bank / wallet fees used in profit calculations.</p>
      </div>

      {/* Default commission */}
      <section className="rounded-md border border-border bg-card p-5">
        <div className="mb-4 flex items-center gap-2"><Percent className="h-5 w-5 text-[#2b9bb7]" /><h3 className="font-heading text-lg font-medium">Default Commission Rate</h3></div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-2">
            <Label>Default affiliate rate (%)</Label>
            <Input type="number" value={defaultRate} onChange={(e) => setDefaultRate(e.target.value)} className="w-40" data-testid="default-rate-input" />
          </div>
          <Button onClick={saveDefault} disabled={busy} data-testid="save-default-rate">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save
          </Button>
          <span className="text-xs text-muted-foreground">Applied to any affiliate without a custom rate below.</span>
        </div>
      </section>

      {/* Protected fields on import */}
      <section className="rounded-md border border-border bg-card p-5" data-testid="protected-fields-section">
        <div className="mb-1 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#2b9bb7]" /><h3 className="font-heading text-lg font-medium">Protected Fields on Import</h3></div>
        <p className="mb-4 text-xs text-muted-foreground">
          Ticked fields are never overwritten by an import once they have a value — so corrections made in the portal always survive fresh CRM uploads. Fields edited manually on a customer profile are additionally locked per customer, automatically.
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {(protectedData?.options || []).map((f) => (
            <label key={f} className="flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-[#EDF3F5]" data-testid={`protected-option-${f}`}>
              <input
                type="checkbox"
                checked={(protectedSel || []).includes(f)}
                onChange={() => toggleProtected(f)}
                className="h-4 w-4 accent-[#2b9bb7]"
              />
              {FIELD_LABELS[f] || f}
            </label>
          ))}
        </div>
        <div className="mt-4">
          <Button onClick={saveProtected} disabled={busy || !protectedSel} data-testid="save-protected-fields">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save protected fields
          </Button>
        </div>
      </section>

      {/* Bank fees */}
      <section className="rounded-md border border-border bg-card p-5">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2"><Landmark className="h-5 w-5 text-[#2b9bb7]" /><h3 className="font-heading text-lg font-medium">Banks &amp; Fees (Provider Mocks)</h3></div>
          <Button variant="outline" size="sm" onClick={syncProviders} disabled={busy} data-testid="sync-providers-button">
            <RefreshCw className="mr-2 h-3.5 w-3.5" /> Sync providers from transactions
          </Button>
        </div>
        <p className="mb-4 text-xs text-muted-foreground">
          Every provider/mock found in your transaction imports is added here automatically — just insert the cost each one charges you (percent and/or fixed) so the system can calculate net profit.
        </p>
        <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-5" data-testid="add-fee-row">
          <Input placeholder="Provider (e.g. bvnk.mock)" value={newFee.provider} onChange={(e) => setNewFee({ ...newFee, provider: e.target.value })} data-testid="fee-provider-input" />
          <Input placeholder="Currency (ALL)" value={newFee.currency} onChange={(e) => setNewFee({ ...newFee, currency: e.target.value })} data-testid="fee-currency-input" />
          <Input type="number" placeholder="Percent %" value={newFee.percent} onChange={(e) => setNewFee({ ...newFee, percent: e.target.value })} data-testid="fee-percent-input" />
          <Input type="number" placeholder="Fixed" value={newFee.fixed} onChange={(e) => setNewFee({ ...newFee, fixed: e.target.value })} data-testid="fee-fixed-input" />
          <Button onClick={addFee} disabled={busy} data-testid="add-fee-button"><Plus className="mr-2 h-4 w-4" /> Add</Button>
        </div>
        {/* Mobile: cards */}
        <div className="space-y-3 md:hidden" data-testid="bank-fees-cards">
          {fees.length === 0 ? (
            <div className="rounded-md border border-border bg-card px-4 py-8 text-center text-muted-foreground">No bank fees yet. Add one above.</div>
          ) : (
            fees.map((f, i) => <FeeCard key={f.id} fee={f} index={i} onSave={saveFee} onDelete={deleteFee} />)
          )}
        </div>
        <div className="hidden overflow-x-auto rounded-md border border-border md:block">
          <table className="w-full text-sm" data-testid="bank-fees-table">
            <thead>
              <tr className="border-b border-border bg-[#EDF3F5] text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Provider</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Currency</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Percent</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fixed</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {fees.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No bank fees yet. Add one above.</td></tr>
              ) : (
                fees.map((f, i) => (
                  <FeeRow key={f.id} fee={f} index={i} onSave={saveFee} onDelete={deleteFee} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Affiliate rate overrides */}
      <section className="rounded-md border border-border bg-card p-5">
        <div className="mb-4 flex items-center gap-2"><Users className="h-5 w-5 text-[#2b9bb7]" /><h3 className="font-heading text-lg font-medium">Affiliate Commission Overrides</h3></div>
        <p className="mb-4 text-xs text-muted-foreground">Set a custom % for specific affiliates. Leave blank to use the default ({settings?.default_referral_rate ?? 30}%).</p>
        {/* Mobile: cards */}
        <div className="space-y-3 md:hidden" data-testid="affiliate-rates-cards">
          {referrals.length === 0 ? (
            <div className="rounded-md border border-border bg-card px-4 py-8 text-center text-muted-foreground">No affiliates yet. Import customers first.</div>
          ) : (
            referrals.map((r, i) => <RateCard key={r.referral} referral={r} rate={rates[r.referral]} defaultRate={settings?.default_referral_rate ?? 30} onSave={saveRate} index={i} />)
          )}
        </div>
        <div className="hidden overflow-x-auto rounded-md border border-border md:block">
          <table className="w-full text-sm" data-testid="affiliate-rates-table">
            <thead>
              <tr className="border-b border-border bg-[#EDF3F5] text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Affiliate Code</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Clients</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Commission %</th>
              </tr>
            </thead>
            <tbody>
              {referrals.length === 0 ? (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">No affiliates yet. Import customers first.</td></tr>
              ) : (
                referrals.map((r, i) => (
                  <RateRow key={r.referral} referral={r} rate={rates[r.referral]} defaultRate={settings?.default_referral_rate ?? 30} onSave={saveRate} index={i} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function RateRow({ referral, rate, defaultRate, onSave, index }) {
  const [val, setVal] = useState(rate != null ? String(rate) : "");
  useEffect(() => { setVal(rate != null ? String(rate) : ""); }, [rate]);
  const dirty = (rate != null ? String(rate) : "") !== val;
  return (
    <tr className="border-b border-border" data-testid={`rate-row-${index}`}>
      <td className="px-4 py-3 font-mono font-medium">{referral.referral}</td>
      <td className="px-4 py-3 text-right tabular">{fmtNum(referral.clients)}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder={`${defaultRate} (default)`}
            className="w-32"
            data-testid={`rate-input-${index}`}
          />
          {dirty && (
            <Button size="sm" onClick={() => onSave(referral.referral, val)} data-testid={`save-rate-${index}`}>
              <Save className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function FeeRow({ fee, index, onSave, onDelete }) {
  const [currency, setCurrency] = useState(fee.currency);
  const [percent, setPercent] = useState(String(fee.percent));
  const [fixed, setFixed] = useState(String(fee.fixed));
  useEffect(() => { setCurrency(fee.currency); setPercent(String(fee.percent)); setFixed(String(fee.fixed)); }, [fee]);
  const dirty = currency !== fee.currency || percent !== String(fee.percent) || fixed !== String(fee.fixed);
  return (
    <tr className="border-b border-border" data-testid={`fee-row-${index}`}>
      <td className="px-4 py-3 font-mono font-medium">{fee.provider}</td>
      <td className="px-4 py-3">
        <Input value={currency} onChange={(e) => setCurrency(e.target.value)} className="h-9 w-24" data-testid={`fee-currency-edit-${index}`} />
      </td>
      <td className="px-4 py-3">
        <Input type="number" value={percent} onChange={(e) => setPercent(e.target.value)} className="h-9 w-24 text-right" data-testid={`fee-percent-edit-${index}`} />
      </td>
      <td className="px-4 py-3">
        <Input type="number" value={fixed} onChange={(e) => setFixed(e.target.value)} className="h-9 w-24 text-right" data-testid={`fee-fixed-edit-${index}`} />
      </td>
      <td className="px-4 py-3">
        <div className="flex justify-end gap-2">
          {dirty && (
            <Button size="sm" onClick={() => onSave(fee.id, { currency: currency.trim() || "ALL", percent: parseFloat(percent) || 0, fixed: parseFloat(fixed) || 0 })} data-testid={`save-fee-${index}`}>
              <Save className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => onDelete(fee.id)} className="text-destructive hover:bg-destructive/10" data-testid={`delete-fee-${index}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

function FeeCard({ fee, index, onSave, onDelete }) {
  const [currency, setCurrency] = useState(fee.currency);
  const [percent, setPercent] = useState(String(fee.percent));
  const [fixed, setFixed] = useState(String(fee.fixed));
  useEffect(() => { setCurrency(fee.currency); setPercent(String(fee.percent)); setFixed(String(fee.fixed)); }, [fee]);
  const dirty = currency !== fee.currency || percent !== String(fee.percent) || fixed !== String(fee.fixed);
  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid={`fee-card-${index}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="truncate font-mono font-medium">{fee.provider}</span>
        <div className="flex shrink-0 gap-2">
          {dirty && (
            <Button size="sm" onClick={() => onSave(fee.id, { currency: currency.trim() || "ALL", percent: parseFloat(percent) || 0, fixed: parseFloat(fixed) || 0 })} data-testid={`save-fee-m-${index}`}>
              <Save className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => onDelete(fee.id)} className="text-destructive hover:bg-destructive/10" data-testid={`delete-fee-m-${index}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Currency</Label>
          <Input value={currency} onChange={(e) => setCurrency(e.target.value)} className="h-9" data-testid={`fee-currency-edit-m-${index}`} />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Percent</Label>
          <Input type="number" value={percent} onChange={(e) => setPercent(e.target.value)} className="h-9" data-testid={`fee-percent-edit-m-${index}`} />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Fixed</Label>
          <Input type="number" value={fixed} onChange={(e) => setFixed(e.target.value)} className="h-9" data-testid={`fee-fixed-edit-m-${index}`} />
        </div>
      </div>
    </div>
  );
}

function RateCard({ referral, rate, defaultRate, onSave, index }) {
  const [val, setVal] = useState(rate != null ? String(rate) : "");
  useEffect(() => { setVal(rate != null ? String(rate) : ""); }, [rate]);
  const dirty = (rate != null ? String(rate) : "") !== val;
  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid={`rate-card-${index}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="truncate font-mono font-medium">{referral.referral}</span>
        <span className="text-xs text-muted-foreground">{fmtNum(referral.clients)} clients</span>
      </div>
      <div className="flex items-end gap-2">
        <div className="flex-1 space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Commission %</Label>
          <Input
            type="number"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder={`${defaultRate} (default)`}
            className="h-9"
            data-testid={`rate-input-m-${index}`}
          />
        </div>
        {dirty && (
          <Button size="sm" onClick={() => onSave(referral.referral, val)} data-testid={`save-rate-m-${index}`}>
            <Save className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}



